import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Safe localStorage wrapper for Safari compatibility
const safeLocalStorage = {
  getItem: (key) => {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      console.warn('localStorage not available:', e);
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (e) {
      console.warn('localStorage not available:', e);
      return false;
    }
  },
  removeItem: (key) => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.warn('localStorage not available:', e);
    }
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => safeLocalStorage.getItem('equibook_token'));
  const [loading, setLoading] = useState(true);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  useEffect(() => {
    // Only run on initial load, not on every token change
    if (initialLoadDone) return;
    
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Check if we have impersonating user data in localStorage (from Super Admin access)
      const impersonatingUserData = safeLocalStorage.getItem('equibook_impersonating_user');
      if (impersonatingUserData) {
        try {
          const userData = JSON.parse(impersonatingUserData);
          setUser(userData);
          setLoading(false);
          setInitialLoadDone(true);
          // Clear after loading (we'll rely on fetchUser for subsequent loads)
          safeLocalStorage.removeItem('equibook_impersonating_user');
          return;
        } catch (e) {
          console.error('Error parsing impersonating user data:', e);
          safeLocalStorage.removeItem('equibook_impersonating_user');
        }
      }
      
      fetchUser();
    } else {
      setLoading(false);
      setInitialLoadDone(true);
    }
  }, [token, initialLoadDone]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Error fetching user:', error);
      logout();
    } finally {
      setLoading(false);
      setInitialLoadDone(true);
    }
  };

  // Direct login with token and user (used after email verification)
  const login = (tokenOrEmail, userDataOrPassword) => {
    // If called with token string and user object directly
    if (typeof userDataOrPassword === 'object' && userDataOrPassword !== null) {
      const accessToken = tokenOrEmail;
      const userData = userDataOrPassword;
      safeLocalStorage.setItem('equibook_token', accessToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      setToken(accessToken);
      setUser(userData);
      return userData;
    }
    // Legacy: called with email and password (for login page)
    return loginWithCredentials(tokenOrEmail, userDataOrPassword);
  };

  const loginWithCredentials = async (email, password) => {
    console.log('loginWithCredentials called for:', email);
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    console.log('Login response received:', response.data);
    const { access_token, user: userData } = response.data;
    
    // Store token
    const stored = safeLocalStorage.setItem('equibook_token', access_token);
    console.log('Token stored in localStorage:', stored);
    
    // Set axios default header
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    
    // Update state - set user first, then token to avoid race condition
    setUser(userData);
    setToken(access_token);
    setLoading(false);
    setInitialLoadDone(true);
    
    console.log('Auth state updated, user:', userData?.email);
    return userData;
  };

  const register = async (userData) => {
    // New flow: registration returns success message, not token
    const response = await axios.post(`${API_URL}/api/auth/register`, userData);
    return response.data; // { success, message, email, requires_verification }
  };

  const updateProfile = async (profileData) => {
    const response = await axios.put(`${API_URL}/api/auth/profile`, profileData);
    setUser(response.data);
    return response.data;
  };

  const logout = () => {
    safeLocalStorage.removeItem('equibook_token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  const isAdmin = () => user?.role_club === 'instructeur_admin';
  const isInstructor = () => ['instructeur', 'instructeur_admin'].includes(user?.role_club);
  const isApproved = () => user?.is_approved === 'approuve' && user?.statut === 'actif';
  const isSuperAdmin = () => user?.system_role === 'super_admin';

  const value = {
    user,
    token,
    loading,
    login,
    loginWithCredentials,
    register,
    logout,
    updateProfile,
    isAdmin,
    isSuperAdmin,
    isInstructor,
    isApproved,
    fetchUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
